/* eslint-disable react/no-unescaped-entities */
import { FooterDataType } from "@/components/organisms/Footer";
import { PageTemplate } from "@/components/organisms/PageTemplate";
import { footerData_en, footerData_us } from "@/shared/data/footer.data";
import { footerData_kr } from "@/shared/data/footer.data.kr";
import { NavbarDataType, navbarData_en, navbarData_us } from "@/shared/data/navbar.data";
import { navbarData_kr } from "@/shared/data/navbar.data.kr";
import { GetStaticProps, InferGetStaticPropsType } from "next";
import Head from "next/head";
import Link from "next/link";

export const getStaticProps: GetStaticProps<{
  navbarData: NavbarDataType;
  footerData: FooterDataType;
}> = async (context) => {
  const locale = (context.locale as "en" | "kr" | "us" | "in") ?? "en";

  const navbarData = {
    en: navbarData_en,
    us: navbarData_us,
    kr: navbarData_kr,
    in: navbarData_en,
  }[locale];

  const footerData = {
    en: footerData_en,
    us: footerData_us,
    kr: footerData_kr,
    in: footerData_en,
  }[locale];

  // const pageData = await getPageBySlug(
  //   "privacy",
  //   ["metaTitle", "metaDescription", "content"],
  //   locale
  // );

  return {
    props: {
      navbarData,
      footerData,
    },
  };
};
export default function UserGuidelinesPage({
  navbarData,
  footerData,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <PageTemplate navbarData={navbarData} footerData={footerData}>
      <Head>
        <title>User Guidelines - Graphy</title>
        <meta name="description" content="Graphy User Guidelines" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>
      <div className="link-dash  flex items-center justify-center px-6 pb-20">
        <div className="WordSection1 max-w-3xl">
          <h1 className="mx-auto mt-24 text-center text-5xl font-extrabold text-black">
            Graphy User Guidelines
          </h1>
          <h2 className="mb-10 pt-6 text-center text-xl uppercase tracking-ultrawide text-black">
            Last Updated: June 20, 2022
          </h2>

          <p className="mb-3 text-justify text-base">
            Being a Platform that enables building communities of people based
            on knowledge-sharing, we, at Graphy, believe in creating a conducive
            environment for learning and communication. To ensure all
            stakeholders, including the Users and representatives of Graphy,
            interacting with each other in connection with the Platform and
            during the use of the Platform have a safe and positive experience,
            we have created these User Guidelines. These User Guidelines read
            with our Platform Terms{" "}
            <i>
              (as defined in the{" "}
              <Link href="/legal/terms">Terms of Service</Link>)
            </i>
            , will help you understand the dos and don'ts in respect of your
            conduct on the Platform or otherwise during your interaction with
            anyone in association with the Platform.
          </p>
          <p className="mb-3 text-justify text-base">
            <b>Please note:</b> We are not obligated to monitor any content
            published on the Platform or review the conduct occurring on or
            through the Platform, however, if any violation of these User
            Guidelines comes to our notice, then, we reserve the right to refuse
            your access to the Platform, terminate accounts or remove such
            violating content at any time without notice to you. If you come
            across any violation of these User Guidelines, you may notify us at{" "}
            <Link href="mailto:@care@graphy.com">care@graphy.com</Link>{" "}
            along with the details of such violations.
          </p>

          <div className="mb-3 text-justify text-base">
            <ol className="ml-6 list-outside list-decimal">
              <li className="pl-2 md:pl-4">
                <b>Legitimate usage of the Platform:</b> You agree to use the
                Platform only for lawful purposes and you are not allowed to use
                our Platform to engage in any kind of activity that violates any
                applicable central, state, local, federal or international law
                or regulation (including, without limitation, any laws regarding
                the export of data or software to and from the USA or other
                countries). Also, you agree that you will not use the Platform
                in any manner that would disrupt, damage or impair the Platform
                or access to it, in any manner, including promoting or
                encouraging illegal activity such as hacking, cracking or
                distribution of counterfeit software, compromised accounts, or
                cheats or hacks for the Platform and conduct of any form of
                fraudulent activity. The sale and promotion of any regulated or
                illegal goods is not allowed. The Platform is to be used only
                for the permitted uses as detailed under this Agreement.
              </li>
              <li className="pl-2 md:pl-4">
                <b>No harmful or dangerous content:</b> We believe that our
                Platform is a safe space for all Users and would like your help
                in ensuring that it remains so. Keeping this in mind, any
                content which incites or promotes violence, that may cause
                physical or emotional harm or that may endanger the safety of
                any individual or is otherwise objectionable is expressly
                prohibited on the Platform.
              </li>
              <li className="pl-2 md:pl-4">
                <b>No hateful or defamatory content:</b> We realise that there
                may be instances of exchange of ideas and opinions which is
                essential in the learning process, while we agree that
                individuals have the right to voice their opinion, we do not
                encourage or tolerate any form of hate speech or statements that
                are libelous, slanderous, threatening, violent, predatory,
                defamatory, or any statement that incites hatred against
                specific individuals or groups with respect to but not limited
                to race or ethnic origin, country caste, religion, disability,
                gender, age, sexual orientation/gender identity etc.
              </li>
              <li className="pl-2 md:pl-4">
                <b>Violent and graphic content:</b> Any content whose sole
                objective is to sensationalise, shock or disturb individuals is
                not allowed. We do not allow any content that promotes terrorist
                acts or incites violence, to be uploaded on the Platform in any
                manner.
              </li>
              <li className="pl-2 md:pl-4">
                <b>Harassment and bullying:</b> Graphy Platform will be used by
                many Users on a daily basis and it is important to be respectful
                and kind to your fellow Users. We do not tolerate any form of
                harassment or bullying on the Platform and strive to keep the
                Platform a safe space to foster learning. Harassment in this
                case would include, without limitation, to abusive videos,
                comments, messages, revealing someone's personal information,
                including sensitive personally identifiable information of
                individuals, content or comments uploaded in order to humiliate
                someone, sexual harassment or sexual bullying in any form.
              </li>
              <li className="pl-2 md:pl-4">
                <b>Spam:</b> Posting untargeted, unwanted, and repetitive
                content, comments or messages with an intention to spam the
                Platform and to drive traffic from the Platform to other
                third-party sites is in direct violation of your Agreement with
                us. Posting links to external websites with malware and other
                prohibited sites is not allowed.
              </li>
              <li className="pl-2 md:pl-4">
                <b>Scams:</b> Any content uploaded or posted in order to trick
                others for their own financial gain is not allowed and we do not
                tolerate any practices of extortion or blackmail, either.
              </li>
              <li className="pl-2 md:pl-4">
                <b>Privacy violation:</b> Kindly refer to our Privacy Policy to
                know how to protect your privacy and respect the privacy of
                other Users.
              </li>
              <li className="pl-2 md:pl-4">
                <b>Impersonation:</b> Impersonating another person, including
                but not limited to, Graphy, a Graphy employee, or another User,
                is not permitted while using our Platform. In this case
                impersonation would mean the intention to cause confusion
                regarding who the real person is by pretending to be them (such
                as using names, image, documents, certificates etc., not
                belonging to you or not used to identify you, or pretending to
                be a company, institute, group etc., by using their logo, brand
                name or any distinguishing mark).
              </li>
              <li className="pl-2 md:pl-4">
                <b>Unauthorized Access or Disabling of Platform:</b> You agree
                not to (i) use the Platform in any manner that could disable,
                overburden, damage, or impair the Platform or interfere with any
                other User's use of the Platform; (ii) not to use any manual
                process to monitor or copy any of the material on the Platform
                or for any other unauthorized purpose without Graphy's prior
                express written consent; (iii) use any device, software, or
                routine that interferes with the proper working of the Platform;
                (iv) attack the Platform via a denial-of- service attack; (v)
                attempt to gain unauthorized access to, interfere with, or
                disrupt any parts of the Platform, the server on which the
                Platform is stored, or any server, computer, or database
                connected to or associated with the Platform; and (vi) introduce
                any viruses, trojan horses, worms, keystroke loggers, malware,
                or other material which is malicious or technologically harmful
                to the Platform.
              </li>
              <li className="pl-2 md:pl-4">
                <b>Interaction outside the Platform:</b> In association with
                your use of the Platform, you may interact with various
                stakeholders, including Graphy's representatives, either through
                or outside the Platform. During such interaction with any
                person, you shall conduct yourself professionally, and you shall
                not conduct yourself in a manner that would amount to any form
                of discrimination, harassment or bullying of such person you
                interact with, including without limitation, any discrimination
                or harassment based on one's religion, caste, creed, ethnicity,
                sex, sexual orientation or preference, gender, gender identity,
                race, colour, national origin, citizenship status, ancestry,
                age, marital status, pregnancy, childbirth, any medical
                conditions, or community. Any claims made or actions taken
                against you as a result of your resulting your conduct shall
                solely be your responsibility and Graphy disclaims any and all
                such liabilities.
              </li>
            </ol>
          </div>
        </div>
      </div>
    </PageTemplate>
  );
}
